import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';
// import 'react-image-gallery/styles/css/image-gallery.css';

import { useIntl } from '../../../util/reactIntl';
import { AspectRatioWrapper, Modal, ResponsiveImage } from '../../../components';
import ReactPlayer from 'react-player/lazy';
import css from './GalleryLayout.module.css';
import { ReactComponent as ForwardIcon } from '../../../assets/svg/Forward.svg';
const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  showFullscreenButton: true,
};
import { ReactComponent as MuteIcon } from '../../../assets/svg/mute.svg';
import { ReactComponent as UnmuteIcon } from '../../../assets/svg/unmute.svg';

const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) return { aspectWidth: 1, aspectHeight: 1 };

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const aspectRatio = w / h;

  return w && h && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : w && h && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
      ? { aspectWidth: 4, aspectHeight: 3 }
      : w && h
        ? { aspectWidth: w, aspectHeight: h }
        : { aspectWidth: 1, aspectHeight: 1 };
};

const ListingImageGallery = props => {
  const galleryRef = useRef(null);
  const modalGalleryRef = useRef(null);
  const videoRef = useRef(null);
  const modalVideoRef = useRef(null);
  const thumbPlayerRefs = useRef({});
  const intl = useIntl();

  // Detect iOS
  const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isHLSStream = url => {
    if (!url) return false;
    return (
      url.includes('.m3u8') ||
      url.includes('videodelivery.net') ||
      url.includes('cloudflarestream.com')
    );
  };

  // Convert Cloudflare Stream player URL to HLS manifest URL for iOS
  const getVideoUrl = url => {
    if (!url) return url;

    // Check if it's a Cloudflare Stream watch URL
    const cloudflareWatchMatch = url.match(/watch\.cloudflarestream\.com\/([a-zA-Z0-9]+)/);
    if (cloudflareWatchMatch && isIOS) {
      const videoId = cloudflareWatchMatch[1];
      return `https://videodelivery.net/${videoId}/manifest/video.m3u8`;
    }

    return url;
  };

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [vidMute, setVidMute] = useState(true);
  const [vidModalMute, setVidModalMute] = useState(true);
  const [vidPlay, setVidPlay] = useState(false);
  const [currIndex, setCurrIndex] = useState(0);
  const [modalCurrIndex, setModalCurrIndex] = useState(0);
  const [videoThumbnail, setVideoThumbNail] = useState(null);
  const [hoveredThumbIndex, setHoveredThumbIndex] = useState(null);
  const [brokenThumbs, setBrokenThumbs] = useState({});
  const [isMobile, setIsMobile] = useState(
    typeof window === 'undefined' ? false : window.innerWidth <= 768
  );
  const {
    rootClassName,
    className,
    images,
    videos,
    videoThumbnailUrl,
    imageVariants,
    thumbnailVariants,
    pageType,
    onManageDisableScrolling,
    isMuted,
    onSlideChange,
    isMenuOpen,
  } = props;

  const thumbVariants = thumbnailVariants || imageVariants;

  useEffect(() => {
    if (isMuted !== undefined) {
      setVidMute(isMuted);
    }
  }, [isMuted]);

  // Force re-render when videos prop changes
  useEffect(() => {
    if (videos && videos.length > 0) {
      // Force a state update to trigger re-render
      setVidPlay(false);
      setTimeout(() => setVidPlay(true), 100);
    }
  }, [videos]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    setVidPlay(true);
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      // Keep muted initially for iOS compatibility
      setVidModalMute(true);
      setVidMute(true);
    } else {
      setVidModalMute(true);
    }
  }, [isFullscreen, modalCurrIndex]);

  useEffect(() => {
    if (currIndex !== 0) {
      setVidMute(true);
    }
  }, [currIndex]);

  // Handle native video mute state for iOS
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = vidMute;
    }
  }, [vidMute]);

  useEffect(() => {
    if (modalVideoRef.current) {
      modalVideoRef.current.muted = vidModalMute;
    }
  }, [vidModalMute]);

  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);

  // Build image items
  const imageItems = (images || []).map((img, i) => ({
    original: '',
    alt: intl.formatMessage(
      { id: 'ListingImageGallery.imageAltText' },
      { index: i + 1, count: images.length }
    ),
    thumbAlt: intl.formatMessage(
      { id: 'ListingImageGallery.imageThumbnailAltText' },
      { index: i + 1, count: images.length }
    ),
    thumbnail: img.attributes?.variants?.[thumbVariants[0]],
    image: img,
  }));

  // Build video items
  const videoItems = Array.isArray(videos)
    ? videos
      .filter(v => typeof v === 'string' && !v.toLowerCase().endsWith('.webp'))
      .map((video, i) => ({
        type: 'video',
        original: '',
        alt: `Video ${i + 1}`,
        thumbAlt: `Video thumbnail ${i + 1}`,
        thumbnail: { url: videoThumbnailUrl || null, width: 400, height: 300 },
        videoUrl: video,
      }))
    : [];

  const items = [...videoItems, ...imageItems];
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };

  const renderItem = item => {
    if (item.type === 'video') {
      return (
        <div className={css.itemCentering} style={{ width: '100% ', height: '100%' }}>
          {/* {vidMute ? (
            <MuteIcon
              className={css.muteOut}
              onClick={e => {
                e.stopPropagation();
                setVidMute(false);
              }}
            />
          ) : (
            <UnmuteIcon
              className={css.muteOut}
              onClick={e => {
                e.stopPropagation();
                setVidMute(true);
              }}
            />
          )} */}
          {/* </div> */}

          {isIOS && isHLSStream(item.videoUrl) ? (
            <video
              ref={videoRef}
              src={getVideoUrl(item.videoUrl)}
              loop
              autoPlay
              muted={vidMute}
              playsInline
              webkit-playsinline={'true'}
              x-webkit-airplay="allow"
              preload="auto"
              // crossOrigin="anonymous"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <ReactPlayer
              key={item.videoUrl}
              url={item.videoUrl}
              controls={false}
              loop={true}
              playing={vidPlay}
              width="100%"
              height="100%"
              style={{ objectFit: 'cover' }}
              muted={vidMute}
              playsInline
              pip={false}
              config={{
                file: {
                  attributes: {
                    playsInline: true,
                    'webkit-playsinline': 'true',
                    'x-webkit-airplay': 'allow',
                    preload: 'auto',
                    // crossOrigin: 'anonymous',
                  },
                  hlsOptions: {
                    enableWorker: false,
                    lowLatencyMode: false,
                  },
                  forceVideo: true,
                },
              }}
            />
          )}
        </div>
      );
    }
    const fullScreenImgAndVideo = e => {
      if (isMenuOpen) {
        if (e && e.stopPropagation) {
          e.stopPropagation();
        }
        return;
      }
      if (galleryRef.current?.fullScreen) {
        try {
          setIsFullscreen(true);
        } catch (err) {
          console.error('Fullscreen failed:', err);
        }
      } else {
        console.warn('Fullscreen not supported');
      }
    };

    return (
      <div
        className={classNames(css.itemCentering, {
          [css.mobileModalItemCentering]: isMobile,
        })}
        onClick={fullScreenImgAndVideo}
      >
        <div
          className={classNames(css.mediaFrame, {
            [css.mobileModalMediaFrame]: isMobile,
          })}
        >
          <ResponsiveImage
            rootClassName={isMobile ? css.mobileModalMedia : css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            pageType={pageType}
            isMobile={isMobile}
          // style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      </div>
    );
  };

  const renderModalItem = item => {
    if (item.type === 'video') {
      return (
        <div
          className={classNames(css.itemCentering, {
            [css.mobileModalItemCentering]: isMobile,
          })}
          style={{ width: '100% ', height: '100%' }}
        >
          {/* <div className={css.videoThumnailWrapper}>
            <img src={videoThumbnail} />
          </div> */}
          <div className={css.muteContainer}>
            <button
              type="button"
              className={css.circleOverlayBtn}
              onClick={e => {
                e.stopPropagation();
                setVidModalMute(prev => !prev);
              }}
              aria-label={vidModalMute ? 'Unmute' : 'Mute'}
            >
              {vidModalMute ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                  <line x1="16" y1="10" x2="20" y2="14" />
                  <line x1="20" y1="10" x2="16" y2="14" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              )}
            </button>
          </div>

          <div
            className={classNames({
              [css.mobileModalMediaFrame]: isMobile,
            })}
          >
            {isIOS && isHLSStream(item.videoUrl) ? (
              <video
                ref={modalVideoRef}
                className={classNames({ [css.mobileModalVideo]: isMobile })}
                src={getVideoUrl(item.videoUrl)}
                loop
                autoPlay
                muted={vidModalMute}
                playsInline
                webkit-playsinline={'true'}
                x-webkit-airplay="allow"
                preload="auto"
                // crossOrigin="anonymous"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <ReactPlayer
                key={item.videoUrl}
                className={classNames({ [css.mobileModalVideo]: isMobile })}
                url={item.videoUrl}
                controls={false}
                loop={true}
                playing={vidPlay}
                width="100%"
                height="100%"
                style={{ objectFit: 'cover' }}
                muted={vidModalMute}
                playsInline
                pip={false}
                config={{
                  file: {
                    attributes: {
                      playsInline: true,
                      'webkit-playsinline': 'true',
                      'x-webkit-airplay': 'allow',
                      preload: 'auto',
                      // crossOrigin: 'anonymous',
                    },
                    hlsOptions: {
                      enableWorker: false,
                      lowLatencyMode: false,
                    },
                    forceVideo: true,
                  },
                }}
              />
            )}
          </div>
          {/* <>
            <video className={css.firstVideo}> <source src={item.videoUrl} />
            </video>
            <video className={css.secondVideo} controls>
               <source src={item.videoUrl} />
            </video>
          </> */}
        </div>
      );
    }
    const fullScreenImgAndVideo = e => {
      if (isMenuOpen) {
        if (e && e.stopPropagation) {
          e.stopPropagation();
        }
        return;
      }
      if (galleryRef.current?.fullScreen) {
        try {
          if (isMobile) {
            // galleryRef.current.fullScreen();
            setVidMute(true);
          } else {
            setIsFullscreen(true);
          }
        } catch (err) {
          console.error('Fullscreen failed:', err);
        }
      } else {
        console.warn('Fullscreen not supported');
      }
    };

    return (
      <div
        className={classNames(css.itemCentering, {
          [css.mobileModalItemCentering]: isMobile,
        })}
        onClick={fullScreenImgAndVideo}
      >
        <div
          className={classNames(css.mediaFrame, {
            [css.mobileModalMediaFrame]: isMobile,
          })}
        >
          <ResponsiveImage
            rootClassName={isMobile ? css.mobileModalMedia : css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            pageType={pageType}
            isMobile={isMobile}
          // style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      </div>
    );
  };

  const renderThumbInner = item => {
    return item.type === 'video' ? null : (
      <ResponsiveImage
        rootClassName={css.thumb}
        image={item.image}
        alt={item.thumbAlt}
        variants={thumbVariants}
        sizes="88px"
      />
    );
  };

  const handleThumbProgress = index => ({ playedSeconds }) => {
    const player = thumbPlayerRefs.current[index];
    if (playedSeconds >= 3 && player) {
      player.seekTo(0);
    }
  };

  const renderVideoThumbInner = (item, index) => {
    const isHovered = hoveredThumbIndex === index;
    const thumbUrl = brokenThumbs[index] ? null : item?.thumbnail?.url;

    return (
      <div
        className={css.videoThumb}
        onMouseEnter={() => setHoveredThumbIndex(index)}
        onMouseLeave={() => setHoveredThumbIndex(null)}
        onFocus={() => setHoveredThumbIndex(index)}
        onBlur={() => setHoveredThumbIndex(null)}
      >
        {isHovered ? (
          <ReactPlayer
            ref={player => {
              if (player) {
                thumbPlayerRefs.current[index] = player;
              } else {
                delete thumbPlayerRefs.current[index];
              }
            }}
            key={`${item.videoUrl}-thumb-preview`}
            className={css.thumb}
            url={item.videoUrl}
            controls={false}
            playing
            loop
            width="100%"
            height="100%"
            muted
            playsInline
            onProgress={handleThumbProgress(index)}
            config={{
              file: {
                attributes: {
                  playsInline: true,
                  'webkit-playsinline': 'true',
                  preload: 'metadata',
                },
              },
            }}
          />
        ) : (
          <>
            {thumbUrl ? (
              <img
                className={css.videoThumbImage}
                src={thumbUrl}
                alt={item.thumbAlt}
                onError={() => {
                  setBrokenThumbs(prev => ({ ...prev, [index]: true }));
                }}
              />
            ) : (
              <video
                className={css.thumb}
                src={item.videoUrl}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
              />
            )}
            <span className={css.videoThumbPlayBadge} aria-hidden="true">
              ▶
            </span>
          </>
        )}
      </div>
    );
  };

  const onScreenChange = isFull => setIsFullscreen(isFull);

  const renderMobileGalleryNav = direction => () => {
    if (!isMobile || items.length <= 1) {
      return null;
    }

    const isPrevious = direction === 'left';
    const targetIndex = isPrevious ? modalCurrIndex - 1 : modalCurrIndex + 1;
    const disabled = targetIndex < 0 || targetIndex >= items.length;

    const navClasses = classNames(css.mobileModalNavButton, {
      [css.mobileModalNavLeft]: isPrevious,
      [css.mobileModalNavRight]: !isPrevious,
      [css.hideButton]: disabled,
    });

    return (
      <button
        type="button"
        className={navClasses}
        disabled={disabled}
        onClick={() => {
          if (!disabled && modalGalleryRef.current) {
            modalGalleryRef.current.slideToIndex(targetIndex);
          }
        }}
        aria-label={isPrevious ? 'Previous media' : 'Next media'}
      >
        <span className={css.mobileModalNavIcon} aria-hidden="true">
          {isPrevious ? '‹' : '›'}
        </span>
      </button>
    );
  };

  // Nav renderers that accept an index
  // const makeLeftNav = currentIndex => (onClick, disabled) => {
  //   if (currentIndex === 0) return null;
  //   return (
  //     <button className={`${css.thumbNav} ${css.upNav}`} disabled={disabled} onClick={onClick}>
  //       <ForwardIcon className={css.leftClick} />
  //     </button>
  //   );
  // };

  // const makeRightNav = (currentIndex, length) => (onClick, disabled) => {
  //   if (currentIndex === length - 1) return null;
  //   return (
  //     <button className={`${css.thumbNav} ${css.downNav}`} disabled={disabled} onClick={onClick}>
  //       <ForwardIcon />
  //     </button>
  //   );
  // };
  const makeLeftNav = currentIndex => (onClick, disabled) => {
    if (disabled || currentIndex === 0 || items.length <= 1) return null;
    return (
      <button
        type="button"
        className={css.cardNavArrowLeft}
        disabled={disabled}
        onClick={e => {
          e.stopPropagation();
          onClick(e);
        }}
        aria-label="Previous"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
    );
  };

  const makeRightNav = (currentIndex, length) => (onClick, disabled) => {
    if (disabled || currentIndex === length - 1 || items.length <= 1) return null;
    return (
      <button
        type="button"
        className={css.cardNavArrowRight}
        disabled={disabled}
        onClick={e => {
          e.stopPropagation();
          onClick(e);
        }}
        aria-label="Next"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    );
  };

  const renderFullscreenButton = () => null;

  const slideToIndex = index => e => {
    e.preventDefault();
    if (galleryRef.current === null) {
      return;
    }
    if (galleryRef.current.state.currentIndex === index) {
      return;
    }
    galleryRef.current.slideToIndex(index);
  };

  const slideModalToIndex = index => e => {
    e.preventDefault();
    if (modalGalleryRef.current === null) {
      return;
    }
    if (modalGalleryRef.current.state.currentIndex === index) {
      return;
    }
    modalGalleryRef.current.slideToIndex(index);
  };

  if (items.length === 0)
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;

  const classes = classNames(rootClassName || css.root, className, css.normalImage);
  const modalGalleryClasses = classNames(css.modalImage, {
    [css.mobileModalImage]: isMobile,
  });

  const renderDotsIndicator = () => {
    if (items.length <= 1) return null;
    return (
      <div className={css.carouselDotsContainer}>
        {items.map((_, index) => (
          <button
            key={`dot-${index}`}
            type="button"
            className={classNames(css.carouselDot, {
              [css.carouselDotActive]: index === currIndex,
            })}
            onClick={e => {
              e.stopPropagation();
              galleryRef.current?.slideToIndex(index);
            }}
            aria-label={`Slide ${index + 1}`}
          />
        ))}
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className={css.imageThumbnailContainer}>
        <ReactImageGallery
          ref={galleryRef}
          onClick={() => {
            setIsFullscreen(true);
            setModalCurrIndex(currIndex);
          }}
          additionalClass={classes}
          items={items}
          renderLeftNav={makeLeftNav(currIndex)}
          renderRightNav={makeRightNav(currIndex, items.length)}
          renderItem={renderItem}
          renderThumbInner={(item, index) =>
            item.type === 'video' ? renderVideoThumbInner(item, index) : renderThumbInner(item)
          }
          onScreenChange={onScreenChange}
          onSlide={e => {
            setCurrIndex(e);
            if (onSlideChange) {
              onSlideChange(e);
            }
          }}
          renderFullscreenButton={renderFullscreenButton}
          {...IMAGE_GALLERY_OPTIONS}
          thumbnailPosition="left"
          infinite={false}
          showThumbnails={false}
          slideDuration={isMobile ? 300 : 450}
        />
        {renderDotsIndicator()}
      </div>
      {(isFullscreen || !isMobile) && (
        <Modal
          modalContainerClassName={css.modalContainer}
          modalScrollLayerClassName={css.modalScrollLayer}
          contentClassName={css.content}
          isOpen={isFullscreen}
          onClose={() => {
            setVidModalMute(true);
            setIsFullscreen(false);
          }}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          {isMobile ? (
            <div className={css.mobileModalShell}>
              <div className={css.mobileModalStage}>
                <ReactImageGallery
                  ref={modalGalleryRef}
                  additionalClass={modalGalleryClasses}
                  items={items}
                  renderLeftNav={renderMobileGalleryNav('left')}
                  renderRightNav={renderMobileGalleryNav('right')}
                  renderItem={renderModalItem}
                  onScreenChange={onScreenChange}
                  onSlide={e => setModalCurrIndex(e)}
                  startIndex={modalCurrIndex}
                  renderFullscreenButton={renderFullscreenButton}
                  {...IMAGE_GALLERY_OPTIONS}
                  showThumbnails={false}
                  slideDuration={200}
                  infinite={false}
                />
              </div>
              <div className={css.mobileModalThumbRail}>
                {items.map((item, index) => (
                  <button
                    key={`modal-thumb-${index}`}
                    type="button"
                    onClick={slideModalToIndex(index)}
                    className={classNames(css.mobileModalThumbButton, {
                      [css.mobileModalThumbActive]: index === modalCurrIndex,
                    })}
                    aria-label={`Open media ${index + 1}`}
                  >
                    {item.type === 'video'
                      ? renderVideoThumbInner(item, index)
                      : renderThumbInner(item)}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <ReactImageGallery
              ref={modalGalleryRef}
              additionalClass={modalGalleryClasses}
              items={items}
              showNav={false}
              renderLeftNav={() => null}
              renderRightNav={() => null}
              renderItem={renderModalItem}
              renderThumbInner={(item, index) =>
                item.type === 'video' ? renderVideoThumbInner(item, index) : renderThumbInner(item)
              }
              onScreenChange={onScreenChange}
              onSlide={e => setModalCurrIndex(e)}
              startIndex={modalCurrIndex}
              renderFullscreenButton={renderFullscreenButton}
              {...IMAGE_GALLERY_OPTIONS}
              thumbnailPosition="left"
              showThumbnails
              slideDuration={250}
              infinite={false}
            />
          )}
        </Modal>
      )}
    </React.Fragment>
  );
};

export default ListingImageGallery;
